// Indicatori — funcții pure peste un array de bare.
//
// Bar = { time, open, high, low, close, volume }
// Fiecare funcție întoarce serii gata de dat lui Lightweight Charts:
// [{ time, value }] sau [{ time, value, color }].

import { candleColors, withAlpha } from '../core/theme.js';

const val = (bar) => bar.close;

/** Media mobilă simplă. */
export function sma(bars, period = 20, pick = val) {
  const out = [];
  let sum = 0;
  for (let i = 0; i < bars.length; i++) {
    sum += pick(bars[i]);
    if (i >= period) sum -= pick(bars[i - period]);
    if (i >= period - 1) out.push({ time: bars[i].time, value: sum / period });
  }
  return out;
}

/** Media mobilă exponențială. */
export function ema(bars, period = 20, pick = val) {
  if (bars.length < period) return [];
  const k = 2 / (period + 1);
  const out = [];
  let prev = 0;
  for (let i = 0; i < period; i++) prev += pick(bars[i]);
  prev /= period;
  out.push({ time: bars[period - 1].time, value: prev });
  for (let i = period; i < bars.length; i++) {
    prev = pick(bars[i]) * k + prev * (1 - k);
    out.push({ time: bars[i].time, value: prev });
  }
  return out;
}

/** Indexul forței relative (Wilder). */
export function rsi(bars, period = 14) {
  if (bars.length <= period) return [];
  const out = [];
  let gain = 0, loss = 0;
  for (let i = 1; i <= period; i++) {
    const d = bars[i].close - bars[i - 1].close;
    if (d >= 0) gain += d; else loss -= d;
  }
  gain /= period; loss /= period;
  const push = (i) => {
    const rs = loss === 0 ? 100 : gain / loss;
    out.push({ time: bars[i].time, value: loss === 0 ? 100 : 100 - 100 / (1 + rs) });
  };
  push(period);
  for (let i = period + 1; i < bars.length; i++) {
    const d = bars[i].close - bars[i - 1].close;
    gain = (gain * (period - 1) + Math.max(0, d)) / period;
    loss = (loss * (period - 1) + Math.max(0, -d)) / period;
    push(i);
  }
  return out;
}

/** MACD: linia, semnalul și histograma. */
export function macd(bars, fast = 12, slow = 26, signal = 9) {
  const fastEma = ema(bars, fast);
  const slowEma = ema(bars, slow);
  if (!fastEma.length || !slowEma.length) return { macd: [], signal: [], histogram: [] };

  const fastMap = new Map(fastEma.map((p) => [p.time, p.value]));
  const line = slowEma
    .filter((p) => fastMap.has(p.time))
    .map((p) => ({ time: p.time, value: fastMap.get(p.time) - p.value }));

  const asBars = line.map((p) => ({ time: p.time, close: p.value }));
  const sig = ema(asBars, signal);
  const sigMap = new Map(sig.map((p) => [p.time, p.value]));

  const histogram = line
    .filter((p) => sigMap.has(p.time))
    .map((p) => {
      const v = p.value - sigMap.get(p.time);
      return { time: p.time, value: v, color: v >= 0 ? '#26a69a80' : '#ef535080' };
    });

  return { macd: line, signal: sig, histogram };
}

/** Benzile Bollinger. */
export function bollinger(bars, period = 20, mult = 2) {
  const mid = sma(bars, period);
  const upper = [], lower = [];
  for (let i = 0; i < mid.length; i++) {
    const end = i + period;
    let variance = 0;
    for (let j = end - period; j < end; j++) variance += (bars[j].close - mid[i].value) ** 2;
    const sd = Math.sqrt(variance / period);
    upper.push({ time: mid[i].time, value: mid[i].value + mult * sd });
    lower.push({ time: mid[i].time, value: mid[i].value - mult * sd });
  }
  return { upper, middle: mid, lower };
}

/** Awesome Oscillator: SMA5 − SMA34 pe prețul median. */
export function awesome(bars, fast = 5, slow = 34) {
  const median = bars.map((b) => ({ time: b.time, close: (b.high + b.low) / 2 }));
  const f = sma(median, fast);
  const s = sma(median, slow);
  const fMap = new Map(f.map((p) => [p.time, p.value]));
  const out = [];
  let prev = null;
  for (const p of s) {
    if (!fMap.has(p.time)) continue;
    const v = fMap.get(p.time) - p.value;
    out.push({
      time: p.time, value: v,
      color: prev === null || v >= prev ? '#26a69a' : '#ef5350',
    });
    prev = v;
  }
  return out;
}

/**
 * Delta cumulativă (CVD) aproximată din lumânări.
 * Fără fluxul de tranzacții nu știm exact ce s-a luat la ask și ce s-a dat la bid,
 * deci împărțim volumul proporțional cu poziția închiderii în interiorul barei.
 * Cu stream-ul de tranzacții pornit, `Chart` suprascrie asta cu delta reală.
 */
export function cvd(bars) {
  const out = [];
  let sum = 0;
  for (const b of bars) {
    const range = b.high - b.low;
    const ratio = range > 0 ? (b.close - b.low) / range : 0.5;
    sum += b.volume * (ratio * 2 - 1);
    out.push({ time: b.time, value: sum });
  }
  return out;
}

/** ATR (Wilder). */
export function atr(bars, period = 14) {
  if (bars.length < period + 1) return [];
  const trs = [];
  for (let i = 1; i < bars.length; i++) {
    const pc = bars[i - 1].close;
    trs.push(Math.max(
      bars[i].high - bars[i].low,
      Math.abs(bars[i].high - pc),
      Math.abs(bars[i].low - pc),
    ));
  }
  const out = [];
  let value = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
  out.push({ time: bars[period].time, value });
  for (let i = period; i < trs.length; i++) {
    value = (value * (period - 1) + trs[i]) / period;
    out.push({ time: bars[i + 1].time, value });
  }
  return out;
}

/** NATR = ATR / preț × 100, ultima valoare. */
export function natr(bars, period = 14) {
  const series = atr(bars, period);
  if (!series.length) return 0;
  const last = bars[bars.length - 1].close;
  return last ? (series[series.length - 1].value / last) * 100 : 0;
}

/** Cât e volumul ultimei bare față de media precedentelor, în procente. */
export function volumeSpike(bars, lookback = 20) {
  if (bars.length < lookback + 1) return 0;
  const recent = bars.slice(-lookback - 1, -1);
  const avg = recent.reduce((a, b) => a + b.volume, 0) / recent.length;
  if (!avg) return 0;
  return (bars[bars.length - 1].volume / avg - 1) * 100;
}

/** Seria de volum colorată după direcția barei (aceleași tonuri ca la ScalpX). */
export function volumeSeries(bars) {
  // Barele de volum sunt corpul lumânării făcut transparent: dacă utilizatorul
  // își alege alte culori, volumul le urmează fără o a doua setare.
  const candles = candleColors();
  const up = withAlpha(candles.up, 0.55);
  const down = withAlpha(candles.down, 0.55);
  return bars.map((b) => ({
    time: b.time,
    value: b.volume,
    color: b.close >= b.open ? up : down,
  }));
}

/** De câte ori prețul a revenit la un nivel după ce s-a format (atingeri distincte). */
function countTouches(bars, price, fromIndex, tolPct) {
  const tol = price * tolPct;
  const lo = price - tol;
  const hi = price + tol;
  let count = 0;
  let inside = false;
  for (let i = fromIndex; i < bars.length; i++) {
    const hit = bars[i].low <= hi && bars[i].high >= lo;
    if (hit && !inside) count++;
    inside = hit;
  }
  return count;
}

/**
 * Niveluri automate de suport/rezistență, după logica ScalpX.
 *
 * Un pivot e un maxim (sau minim) local pe o fereastră de `wing` bare de fiecare parte.
 * Un nivel e „rupt" dacă prețul a depășit ulterior acel vârf — acelea se desenează mai
 * palid, nu se ascund, pentru că zonele rupte rămân relevante.
 *
 * Fiecare nivel știe de unde începe (`index`, `time`), ca linia să pornească din bara
 * care l-a format și să meargă spre dreapta — nu pe toată lățimea.
 */
export function autoLevels(bars, {
  wing = 3, maxLevels = 8, mergePct = 0.004, touchPct = 0.0005, showBroken = true,
} = {}) {
  const n = bars.length;
  if (n < wing * 2 + 5) return [];

  // Maximul/minimul din viitorul fiecărei bare — spune dacă nivelul a fost depășit.
  const futureHigh = new Array(n);
  const futureLow = new Array(n);
  futureHigh[n - 1] = -Infinity;
  futureLow[n - 1] = Infinity;
  for (let i = n - 2; i >= 0; i--) {
    futureHigh[i] = Math.max(bars[i + 1].high, futureHigh[i + 1]);
    futureLow[i] = Math.min(bars[i + 1].low, futureLow[i + 1]);
  }

  const found = [];
  for (let i = wing; i < n - wing; i++) {
    const hi = bars[i].high;
    const lo = bars[i].low;
    let isHigh = true;
    let isLow = true;
    for (let j = i - wing; j <= i + wing; j++) {
      if (j === i) continue;
      if (bars[j].high >= hi) isHigh = false;
      if (bars[j].low <= lo) isLow = false;
      if (!isHigh && !isLow) break;
    }
    if (isHigh) {
      const broken = futureHigh[i] > hi;
      if (!broken || showBroken) found.push({ price: hi, index: i, time: bars[i].time, broken, kind: 'resistance' });
    }
    if (isLow) {
      const broken = futureLow[i] < lo;
      if (!broken || showBroken) found.push({ price: lo, index: i, time: bars[i].time, broken, kind: 'support' });
    }
  }
  if (!found.length) return [];

  // Cele mai recente au prioritate; nivelurile apropiate se contopesc.
  found.sort((a, b) => b.index - a.index);
  const kept = [];
  for (const level of found) {
    if (kept.some((k) => Math.abs(k.price - level.price) / level.price < mergePct)) continue;
    kept.push(level);
    if (kept.length >= maxLevels) break;
  }

  return kept.map((level) => ({
    ...level,
    touches: countTouches(bars, level.price, level.index + 1, touchPct),
  }));
}

/** Metricile din bara „технические данные" de deasupra graficului. */
export function techData(bars) {
  if (!bars.length) return null;
  const last = bars[bars.length - 1];
  const first = bars[0];
  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  const high = Math.max(...highs);
  const low = Math.min(...lows);
  return {
    volume: bars.reduce((a, b) => a + b.volume, 0),
    change: first.open ? ((last.close - first.open) / first.open) * 100 : 0,
    volatility: low ? ((high - low) / low) * 100 : 0,
    natr: natr(bars),
    volumeSpike: volumeSpike(bars),
    high,
    low,
  };
}
